import { useMemo, useState } from "react";
import type { MarketCatalog, MarketPriceItem } from "@/api/market";
import { ChipTab } from "@/ui/wx";
import { fmtUsd } from "@/lib/format";

const FAV_KEY = "nanom.market.favorites";

function loadFavs(): Set<string> {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveFavs(s: Set<string>) {
  localStorage.setItem(FAV_KEY, JSON.stringify([...s]));
}

export function MarketList({
  catalog,
  prices,
  onTrade,
}: {
  catalog: MarketCatalog | null;
  prices: Record<string, MarketPriceItem>;
  onTrade?: (symbol: string, network: string) => void;
}) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [favs, setFavs] = useState<Set<string>>(() => loadFavs());
  const [onlyFav, setOnlyFav] = useState(false);

  const tokens = useMemo(() => {
    let list = catalog?.tokens || [];
    if (filter !== "all") list = list.filter((t) => t.networkCode === filter);
    const qq = q.trim().toLowerCase();
    if (qq) {
      list = list.filter(
        (t) =>
          t.symbol.toLowerCase().includes(qq) ||
          (t.name || "").toLowerCase().includes(qq) ||
          t.networkCode.toLowerCase().includes(qq)
      );
    }
    if (onlyFav) list = list.filter((t) => favs.has(`${t.networkCode}:${t.symbol}`));
    return list;
  }, [catalog, filter, q, onlyFav, favs]);

  const movers = useMemo(() => {
    const rows = (catalog?.tokens || [])
      .map((t) => {
        const p = prices[`${t.networkCode}:${t.symbol}`];
        const n = Number(String(p?.priceUsd ?? "").replace(/,/g, ""));
        return { t, n: Number.isFinite(n) ? n : 0 };
      })
      .filter((r) => r.n > 0)
      .sort((a, b) => b.n - a.n);
    const gain = rows[0];
    const loss = rows[rows.length - 1];
    return { gain, loss };
  }, [catalog, prices]);

  function toggleFav(key: string) {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveFavs(next);
      return next;
    });
  }

  return (
    <div className="wx-market-list" dir="rtl">
      <div className="wx-movers">
        {movers.gain ? (
          <article className="wx-mover up">
            <span className="muted">بیشترین قیمت</span>
            <b>{movers.gain.t.symbol}</b>
            <span className="ltr">{fmtUsd(movers.gain.n)}</span>
          </article>
        ) : null}
        {movers.loss && movers.loss !== movers.gain ? (
          <article className="wx-mover down">
            <span className="muted">کمترین قیمت</span>
            <b>{movers.loss.t.symbol}</b>
            <span className="ltr">{fmtUsd(movers.loss.n)}</span>
          </article>
        ) : null}
      </div>

      <div className="wx-mode-tabs">
        {["all", "bsc", "nmc"].map((f) => (
          <ChipTab key={f} active={filter === f} onClick={() => setFilter(f)}>
            {f === "all" ? "همه" : f}
          </ChipTab>
        ))}
      </div>

      <div className="wx-search-row">
        <input
          className="wx-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="جستجو"
          aria-label="جستجو"
        />
        <button
          type="button"
          className={`wx-fav-toggle ${onlyFav ? "on" : ""}`}
          onClick={() => setOnlyFav((v) => !v)}
          aria-label="علاقه‌مندی"
        >
          ★
        </button>
      </div>

      <div className="wx-price-list">
        {tokens.map((t) => {
          const key = `${t.networkCode}:${t.symbol}`;
          const p = prices[key];
          const starred = favs.has(key);
          return (
            <article key={key} className="wx-pair-row wx-pair-row-rich">
              <button
                type="button"
                className="wx-pair-main"
                onClick={() => onTrade?.(t.symbol, t.networkCode)}
              >
                <span className="wx-token-dot sm">{t.symbol.slice(0, 1)}</span>
                <div>
                  <b>
                    {t.symbol}
                    <span className="muted"> / USDT</span>
                  </b>
                  <span className="muted">{t.name || t.networkCode}</span>
                </div>
              </button>
              <div className="wx-pair-meta">
                <b className="ltr">{fmtUsd(p?.priceUsd)}</b>
                <button
                  type="button"
                  className={`wx-star ${starred ? "on" : ""}`}
                  onClick={() => toggleFav(key)}
                  aria-label="علاقه‌مندی"
                >
                  ★
                </button>
              </div>
            </article>
          );
        })}
        {!tokens.length && <p className="muted">۰</p>}
      </div>
    </div>
  );
}
